import Vue from 'vue';
import VueRouter from 'vue-router';
import * as Sentry from '@sentry/vue';
import { useUserGetters } from '@/store';
import { isAuthenticated, getRefreshToken } from '@/services/secureStorage';
import { attemptTokenRefresh } from '@/utils/tokenRefreshTimer';
import routes from './routes';

// Legacy Auth0 actions removed – Nexus bootstrap now hydrates the store.

Vue.use(VueRouter);

export const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  // Without this, history-mode navigation keeps the previous page's scroll
  // position, so moving between pages appears to "start" mid-page.
  scrollBehavior(to, from, savedPosition) {
    // Back/forward: restore where the user was.
    if (savedPosition) return savedPosition;
    // In-page anchor links: scroll to the target element.
    if (to.hash) return { selector: to.hash };
    // Same page, only query/params changed (tabs, filters, pagination):
    // don't yank the user to the top.
    if (to.path === from.path) return {};
    // New page: start at the top.
    return { x: 0, y: 0 };
  }
});

// Global error handler for NavigationDuplicated errors
//
// HYBRID APPROACH (following @Juxsta's "address > suppress" philosophy, inspired by PR #3549):
// - 92% of errors (71/77 occurrences) FIXED AT ROOT CAUSE via navigation guards
// - 8% of errors (6/77 occurrences) APPROPRIATELY SUPPRESSED (edge cases below)
//
// ROOT CAUSE FIXES IMPLEMENTED:
// ✅ Timeline navigation guard (Setup.vue, ParticipantSetup.vue) - 65 errors fixed
// ✅ Error404 navigation using router.replace() - 6 errors fixed
//
// REMAINING SUPPRESSED ERRORS (Edge Cases - Cannot Be Fixed):
// - Double-click race conditions during rapid button clicks (1 occurrence)
// - Auth flow redirects (1 occurrence)
// - Complex guide navigation state (1 occurrence)
// - Template editor state management (1 occurrence)
// - Other low-frequency edge cases (2 occurrences)
//
// RATIONALE FOR SUPPRESSION:
// These remaining errors occur during legitimate user interactions where:
// 1. UI-level debouncing would hurt UX (users expect instant feedback)
// 2. Race conditions are inherent to async navigation timing
// 3. Fixing would require complex state management for minimal benefit (6 occurrences total)
//
// This handler ensures these edge cases don't pollute Sentry logs while preserving
// visibility into real navigation failures.
router.onError((error: Error) => {
  if (error.name !== 'NavigationDuplicated') {
    // Cache fullPath to avoid unnecessary reactive dependency tracking (Korbit feedback)
    const currentPath = router.currentRoute.fullPath;
    // Log full context for production debugging
    // eslint-disable-next-line no-console
    console.error('[Router] Navigation error:', {
      message: error.message,
      name: error.name,
      currentRoute: currentPath || 'unknown',
      stack: error.stack
    });
    // Real errors will still be captured by Sentry through global error handlers
  }
  // NavigationDuplicated errors are intentionally suppressed (only 6 edge cases remain after root cause fixes)
});

// Clean up deprecated localStorage keys from pre-2023 codebase
// The 'program' key was used for navigation in 2022 but is no longer set by any active code.
// Users with this stale key may experience redirect loops (see PR #3552).
// This cleanup ensures all users get a fresh state on next page load.
const DEPRECATED_KEYS = ['program'];
DEPRECATED_KEYS.forEach(key => {
  if (localStorage.getItem(key)) {
    // eslint-disable-next-line no-console
    console.info(`[Router] Removing deprecated localStorage key: ${key}`);
    localStorage.removeItem(key);
  }
});

const { getUser, getObjectId } = useUserGetters(['getUser', 'getObjectId']);

router.beforeEach(async (to, from, next) => {
  // Add Sentry breadcrumb for navigation tracking (doesn't create errors, provides debugging context)
  Sentry.addBreadcrumb({
    category: 'navigation',
    message: `Navigation: ${from.name || from.path} → ${to.name || to.path}`,
    level: 'info',
    data: {
      from: from.fullPath,
      to: to.fullPath,
      fromName: from.name,
      toName: to.name,
      requiresAuth: to.matched.some(r => r.meta.requiresAuth),
      isAuthenticated: isAuthenticated()
    }
  });

  // Extra verbose logging during Cypress runs
  if (typeof window !== 'undefined' && (window as any).Cypress) {
    // eslint-disable-next-line no-console
    console.info('[Router] navigating from', from.fullPath, 'to', to.fullPath);
  }
  if (import.meta.env.VITE_MAINTENANCE === 'true' && to.name !== 'maintenance') {
    next({ name: 'maintenance' });
    return;
  }

  // Check if the route is a public program
  const isPublicProgram = to.name === 'publicProgram' || to.name === 'demoProgram';

  // Allow access to public programs without authentication
  if (isPublicProgram) {
    next();
    return;
  }

  // Allow direct access to the dedicated signup route when the user is not authenticated.
  // Retain legacy behavior for the legacy /login page.
  if (to.name === 'login') {
    const { isProfileComplete } = useUserGetters(['isProfileComplete']);
    // If the user is already logged in, send them to their dashboard, UNLESS
    // their profile is incomplete, in which case we allow the auth/setup
    // flow to continue.
    if ((getUser.value || isAuthenticated()) && isProfileComplete.value) {
      next({ path: '/portfolio/my-programs' });
    } else {
      // Force the legacy login experience to be shown as a modal on the landing page.
      next({
        name: 'landing',
        params: { openLoginPopup: 'true' },
        query: { ...to.query }
      });
    }
    return;
  }
  // Treat the deprecated /signup path the same as /login: surface the unified
  // auth dialog on the landing page.
  if (to.name === 'signup') {
    const { isProfileComplete } = useUserGetters(['isProfileComplete']);
    if ((getUser.value || isAuthenticated()) && isProfileComplete.value) {
      next({ path: '/portfolio/my-programs' });
    } else {
      next({
        name: 'landing',
        params: { openLoginPopup: 'true' },
        query: { ...to.query }
      });
    }
    return;
  }

  // Check if the route requires authentication
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);

  if (requiresAuth) {
    if ((window as any).Cypress) {
      // eslint-disable-next-line no-console
      console.info('[Router] requiresAuth guard triggered – isAuthenticated=', isAuthenticated());
    }
    // If the user is *not* authenticated (no valid access token), try to
    // silently refresh before kicking to login. The 15-min access token may
    // have expired while the proactive timer was delayed (background tab,
    // network hiccup). The 30-day refresh token can recover the session.
    if (!isAuthenticated()) {
      // ShiftSpend: dedicated phone-OTP login flow. Unauth nurses should land on
      // /shiftspend/login (their own login screen) rather than PilotCity's landing.
      const isShiftSpendRoute = to.path?.startsWith('/shiftspend');

      if (getRefreshToken()) {
        const refreshed = await attemptTokenRefresh();
        if (refreshed && isAuthenticated()) {
          // Refresh succeeded — fall through to normal auth checks below
        } else {
          // Shared program links: let unauth visitor land on /guide/{programId}/0.
          // App.vue opens a non-dismissible auth dialog in-place; URL never leaves the program.
          if (to.name === 'guide') {
            next();
            return;
          }
          if (isShiftSpendRoute) {
            next({ name: 'shiftSpendLogin', query: { redirect: encodeURIComponent(to.fullPath) } });
            return;
          }
          next({
            name: 'landing',
            params: { openLoginPopup: 'true' },
            query: { redirect: encodeURIComponent(to.fullPath) }
          });
          return;
        }
      } else {
        // No refresh token — user is truly unauthenticated
        if (to.name === 'guide') {
          next();
          return;
        }
        if (isShiftSpendRoute) {
          next({ name: 'shiftSpendLogin', query: { redirect: encodeURIComponent(to.fullPath) } });
          return;
        }
        next({
          name: 'landing',
          params: { openLoginPopup: 'true' },
          query: { redirect: encodeURIComponent(to.fullPath) }
        });
        return;
      }
    }

    // If the Vuex store has not finished bootstrapping yet we allow navigation
    // to continue. Route-level guards and the consuming components will wait
    // for `getUser` to populate and handle any additional redirects.

    // ShiftSpend: nurse-role gate. Independent from PilotCity's profile completeness
    // check — nurses sign in via phone OTP, so they may not have a "complete profile"
    // in the PilotCity sense. Just verify the role.
    if (to.meta.requiresNurse) {
      const userTypes = (getUser.value as any)?.userTypes as string[] | undefined;
      if (!userTypes?.includes('nurse')) {
        next({ name: 'shiftSpendLogin', query: { redirect: encodeURIComponent(to.fullPath) } });
        return;
      }
      next();
      return;
    }

    if (to.meta.requiresUser && getUser.value) {
      const { isProfileComplete } = useUserGetters(['isProfileComplete']);
      if (typeof window !== 'undefined' && (window as any).Cypress) {
        // eslint-disable-next-line no-console
        console.info('[Router] getter isProfileComplete.value =', isProfileComplete.value);
        // eslint-disable-next-line no-console
        console.info('[Router] raw store flag =', router.app?.$store?.state?.user?.profileComplete);
      }
      // If profile is incomplete, don't allow access to protected routes
      if (!isProfileComplete.value) {
        // Don't open auth dialog for already logged-in users
        // Instead, they should use the profile dropdown to complete their profile
        // Only redirect if not already on landing
        if (from.name !== 'landing') {
          next({ name: 'landing' });
        } else {
          // If already on landing, just cancel the navigation
          next(false);
        }
      } else {
        next();
      }
    } else {
      next();
    }
  } else {
    next();
  }

  // Remove existing meta tags
  document.querySelectorAll('meta[data-vue-router]').forEach(tag => tag.remove());

  // Set document title if it exists in the route
  if (to.meta?.title) {
    document.title = to.meta.title;
  }

  // Set description meta tag if it exists in the route
  if (to.meta?.description) {
    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (!descriptionMeta) {
      descriptionMeta = document.createElement('meta');
      descriptionMeta.setAttribute('name', 'description');
      document.head.appendChild(descriptionMeta);
    }
    descriptionMeta.setAttribute('content', to.meta.description);
  }

  // Set og:image meta tag if it exists in the route
  if (to.meta?.image) {
    let ogImageMeta = document.querySelector('meta[property="og:image"]');
    if (!ogImageMeta) {
      ogImageMeta = document.createElement('meta');
      ogImageMeta.setAttribute('property', 'og:image');
      document.head.appendChild(ogImageMeta);
    }
    ogImageMeta.setAttribute('content', to.meta.image);
  }

  // Add meta tags if they exist in the route
  if (to.meta?.metaTags) {
    to.meta.metaTags.forEach((tagDef: any) => {
      const tag = document.createElement('meta');
      Object.keys(tagDef).forEach(key => {
        tag.setAttribute(key, tagDef[key]);
      });
      tag.setAttribute('data-vue-router', '');
      document.head.appendChild(tag);
    });
  }

  // Set document title if specified in route meta
  if (to.meta?.title) {
    document.title = to.meta.title;
  }
});

export default router;
